import { AuthProvider } from '../../contracts';
import { getAuthIdentityConfig } from '../identity';
import { useAuthSessionMetadata } from './useAuthSessionMetadata';
import { useAuthState } from './useAuthState';

export interface IsAuthenticatedState {
  /** Convenience boolean — `true` when the user is authenticated. */
  isAuthenticated: boolean;
  /** Convenience boolean — `true` while the auth state is still resolving. */
  isLoading: boolean;
  /** Provider-breakdown fields preserved for backwards compat / debugging. */
  isKindeAuthenticated: boolean;
  isAuth0Authenticated: boolean;
  isAuth0DesktopAuthenticated: boolean;
}

/**
 * Unified authentication-status hook.
 *
 * Cache-backed for every provider except Kinde-web and FHIR. Kinde-web
 * reads the SDK directly. FHIR carries its own presence gate, because
 * the cache's FHIR branch returns null. The public return shape is the
 * same on every branch.
 */
export const useIsAuthenticated = (): IsAuthenticatedState => {
  // Unconditional: auth state, session metadata, then the app inputs hook
  // (Kinde, Redux provider, external-launch token, persisted token).
  const state = useAuthState();
  const session = useAuthSessionMetadata();
  const {
    reduxProvider,
    legacyAuthToken,
    externalLaunchToken,
    kinde: kindeAuth,
  } = getAuthIdentityConfig().useAuthIdentityInputs();
  const hasPersistedToken = !!legacyAuthToken;

  // Before any provider branch. An external-launch session can carry
  // provider KINDE, and the Kinde-web branch would otherwise report logged-out.
  if (externalLaunchToken) {
    return {
      isAuthenticated: true,
      isLoading: false,
      isKindeAuthenticated: false,
      isAuth0Authenticated: false,
      isAuth0DesktopAuthenticated: false,
    };
  }

  const { getEffectiveAuthProvider, isTauri } = getAuthIdentityConfig();
  const effectiveProvider = getEffectiveAuthProvider();
  const isKindeWebLegacy = !isTauri() && effectiveProvider === AuthProvider.KINDE;

  if (isKindeWebLegacy) {
    const kindeIsAuthenticated = kindeAuth.isAuthenticated ?? false;
    // SDK `isLoading` is a network round-trip. A persisted token stays
    // provisionally authenticated until the SDK's own verdict lands.
    const kindeIsLoading = kindeAuth.isLoading ?? false;
    return {
      isAuthenticated: hasPersistedToken && (kindeIsAuthenticated || kindeIsLoading),
      isLoading: !hasPersistedToken && (kindeIsLoading || kindeIsAuthenticated),
      isKindeAuthenticated: hasPersistedToken && kindeIsAuthenticated,
      isAuth0Authenticated: false,
      isAuth0DesktopAuthenticated: false,
    };
  }

  // Web SDK-init window: provider still null, SDK still loading. Raw
  // truthiness of `isLoading` — null is not this window. A persisted
  // token is provisional; otherwise the consumer stays on the skeleton.
  if (!isTauri() && effectiveProvider === null && kindeAuth.isLoading) {
    if (hasPersistedToken) {
      return {
        isAuthenticated: true,
        isLoading: false,
        isKindeAuthenticated: false,
        isAuth0Authenticated: false,
        isAuth0DesktopAuthenticated: false,
      };
    }
    return {
      isAuthenticated: false,
      isLoading: true,
      isKindeAuthenticated: false,
      isAuth0Authenticated: false,
      isAuth0DesktopAuthenticated: false,
    };
  }

  // FHIR: the cache cannot gate these sessions. Either token slot
  // authenticates, and the launch is synchronous so loading is always false.
  const isFhir = effectiveProvider === AuthProvider.FHIR;
  if (isFhir) {
    const hasFhirToken = !!(externalLaunchToken || legacyAuthToken);
    return {
      isAuthenticated: hasFhirToken,
      isLoading: false,
      isKindeAuthenticated: false,
      isAuth0Authenticated: false,
      isAuth0DesktopAuthenticated: false,
    };
  }

  const isAuthenticated =
    state === 'authenticated' || (state === 'loading' && hasPersistedToken);
  const isLoading = state === 'loading' && !hasPersistedToken;

  // Cookie-first provider, with the subscribed Redux value as the
  // defensive fallback the helper already applies internally.
  const activeProvider = effectiveProvider ?? reduxProvider;
  const authProviderMatches = (target: AuthProvider): boolean =>
    isAuthenticated && session !== null && activeProvider === target;

  return {
    isAuthenticated,
    isLoading,
    isKindeAuthenticated: authProviderMatches(AuthProvider.KINDE),
    isAuth0Authenticated: authProviderMatches(AuthProvider.AUTH0),
    isAuth0DesktopAuthenticated: authProviderMatches(AuthProvider.AUTH0_DESKTOP),
  };
};
